<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="updateRecipient">
            <v-card style="position: relative;">
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Edit Member</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>
                <v-overlay :model-value="!formReady" contained class="align-center justify-center">
                    <v-progress-circular indeterminate color="primary"/>
                </v-overlay>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row v-if="!recipient.type">
                        <v-col cols="12">
                            <v-select v-model="formData['user_id']" :items="users" density="compact"
                                      item-title="first_name" item-value="id" label="User">
                                <template v-slot:item="{ props, item }">
                                    <v-list-item :subtitle="item.raw.email"
                                                 :title="`${item.raw.first_name} ${item.raw.last_name}`"
                                                 v-bind="props"></v-list-item>
                                </template>
                            </v-select>
                        </v-col>
                        <v-col sm="6">
                            <v-switch v-model="formData['app_enabled']" color="primary" density="compact" label="App"/>
                        </v-col>
                        <v-col sm="6">
                            <v-switch v-model="formData['email_enabled']" color="primary" density="compact"
                                      label="Email"/>
                        </v-col>
                    </v-row>
                    <v-row v-else>
                        <v-col cols="6">
                            <v-text-field v-model="formData['recipient']" autofocus :rules="[vRules.required]"
                                          type="email" density="compact" label="Email"/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button" @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const formReady = ref(false);
const users = ref([]);
const errors = ref([]);
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    recipient: Object,
    group: Object,
});

const dialogSwitch = ref(props.modelValue);
const formData = reactive({
    valid: false,
    type: props.recipient.type,
    user_id: props.recipient.user_id,
    app_enabled: !!props.recipient.app_enabled,
    sms_enabled: false,
    email_enabled: props.recipient.type ? true : !!props.recipient.email_enabled,
    recipient: props.recipient.type ? (props.recipient.email ?? null) : null,
});
const emits = defineEmits(['update:modelValue', 'updateRecipient']);

onMounted(() => {
    const promises = [];
    if (props.recipient.type === 0) {
        promises.push(fetchUsers());
    }
    Promise.all(promises).finally(() => formReady.value = true);
})
const fetchUsers = () => {
    return send('get', `companies/${props.group.company_id}/users?list=true&include_self=1`)
        .then((response) => {
            users.value = response.data.data.users;
        })
        .catch(() => {
            snackbar.showSnackbar('Users could not be loaded', 'error')
        })
}
const updateRecipient = () => {
    errors.value = [];
    send('put', `monitoring/notification-group-members/${props.recipient.id}`, formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('updateRecipient', response.data.data.member);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

</script>
